import { useEffect, useRef, useState } from 'react';
import {
  BATCH_TRANSFER_MAX_IMPORT_BYTES,
  BATCH_TRANSFER_MAX_APPLY_CARDS,
  parseChecklistCorrectionsFile,
  type ChecklistCorrectionsFile,
  type ChecklistCorrectionPreview,
  type ChecklistCorrectionApplyRow,
} from '@tcvs/shared';
import type { BatchTransferApi } from './batch-transfer-api';

class CorrectionFileError extends Error {
  constructor(
    message: string,
    readonly reason:
      | 'INVALID_JSON'
      | 'INVALID_FORMAT'
      | 'FILE_TOO_LARGE'
      | 'WRONG_BATCH'
      | 'FILE_READ_FAILED',
  ) {
    super(message);
  }
}
export async function readChecklistCorrectionFile(
  file: File,
  batchId: string,
): Promise<ChecklistCorrectionsFile> {
  if (file.size > BATCH_TRANSFER_MAX_IMPORT_BYTES)
    throw new CorrectionFileError('Choose a JSON file no larger than 2 MiB.', 'FILE_TOO_LARGE');
  const content = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () =>
      reject(new CorrectionFileError('This file could not be read.', 'FILE_READ_FAILED'));
    reader.readAsText(file);
  });
  let value: unknown;
  try {
    value = JSON.parse(content);
  } catch {
    throw new CorrectionFileError('This file is not valid JSON.', 'INVALID_JSON');
  }
  const parsed = parseChecklistCorrectionsFile(value);
  if (!parsed.ok)
    throw new CorrectionFileError(
      `Checklist correction file rejected: ${parsed.error}`,
      'INVALID_FORMAT',
    );
  if (parsed.value.batchId !== batchId)
    throw new CorrectionFileError(
      'This correction file belongs to a different batch.',
      'WRONG_BATCH',
    );
  return parsed.value;
}
function reportReference(body: unknown): string | undefined {
  if (!body || typeof body !== 'object') return undefined;
  const id = (body as Record<string, unknown>).reportId;
  return typeof id === 'string' ? id : undefined;
}
function Values({ value }: { value?: Record<string, unknown> }) {
  return value ? (
    <dl>
      {Object.entries(value).map(([field, item]) => (
        <div key={field}>
          <dt>{field}</dt>
          <dd>{item === null ? 'Clear value' : String(item)}</dd>
        </div>
      ))}
    </dl>
  ) : (
    <span>—</span>
  );
}
/** Catalog corrections are independent of card identity imports. No automatic application. */
export function ChecklistCorrectionTools({
  batchId,
  api,
  admin,
  disabled = false,
  onApplying,
  onReport,
}: {
  batchId: string;
  api: BatchTransferApi;
  admin: boolean;
  disabled?: boolean;
  onApplying: (applying: boolean) => void;
  onReport?: (id: string | null) => void;
}) {
  const [file, setFile] = useState<ChecklistCorrectionsFile | null>(null);
  const [preview, setPreview] = useState<ChecklistCorrectionPreview | null>(null);
  const [outcomes, setOutcomes] = useState<ChecklistCorrectionApplyRow[]>([]);
  const [selected, setSelected] = useState(new Set<string>());
  const [busy, setBusy] = useState<'PREVIEW' | 'APPLY' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const visit = useRef(0);
  const currentBatch = useRef(batchId);
  currentBatch.current = batchId;
  const controllers = useRef(new Set<AbortController>());
  useEffect(() => {
    ++visit.current;
    setFile(null);
    setPreview(null);
    setSelected(new Set());
    setOutcomes([]);
    setBusy(null);
    setError(null);
    setNotice(null);
    return () => {
      ++visit.current;
      for (const controller of controllers.current) controller.abort();
    };
  }, [batchId]);
  const request = () => {
    const version = ++visit.current;
    const batch = batchId;
    const controller = new AbortController();
    controllers.current.add(controller);
    return {
      signal: controller.signal,
      current: () => version === visit.current && batch === currentBatch.current,
      done: () => controllers.current.delete(controller),
    };
  };
  const upload = async (source: File) => {
    onReport?.(null);
    setBusy('PREVIEW');
    setError(null);
    setNotice(null);
    setFile(null);
    setPreview(null);
    setSelected(new Set());
    setOutcomes([]);
    const active = request();
    try {
      const corrections = await readChecklistCorrectionFile(source, batchId);
      if (!active.current()) return;
      const result = await api.previewCorrections(batchId, corrections, active.signal);
      if (!active.current()) return;
      if (!result.ok) {
        const id = reportReference(result.body);
        if (id) onReport?.(id);
        throw new Error(result.message ?? result.error);
      }
      if (result.data.reportId) onReport?.(result.data.reportId);
      setFile(corrections);
      setPreview(result.data);
      setSelected(
        new Set(
          result.data.rows.filter((row) => row.status === 'READY').map((row) => row.correctionId),
        ),
      );
    } catch (cause) {
      if (active.current()) {
        setError(cause instanceof Error ? cause.message : 'Corrections could not be previewed.');
        if (cause instanceof CorrectionFileError) {
          try {
            const logged = await api.logImportFailure(
              batchId,
              cause.reason,
              source.name.slice(0, 255),
              active.signal,
            );
            if (active.current()) {
              if (logged.ok) onReport?.(logged.data.reportId);
              else
                setNotice('The correction file was rejected, but its report could not be saved.');
            }
          } catch {
            if (active.current())
              setNotice('The correction file was rejected, but its report could not be saved.');
          }
        }
      }
    } finally {
      active.done();
      if (active.current()) setBusy(null);
    }
  };
  const apply = async () => {
    if (!admin || !file || !preview || !selected.size || busy !== null) return;
    const active = request();
    const ids = [...selected];
    const confirmed = new Map<string, ChecklistCorrectionApplyRow>();
    setBusy('APPLY');
    onApplying(true);
    setError(null);
    setNotice(null);
    try {
      for (let index = 0; index < ids.length; index += BATCH_TRANSFER_MAX_APPLY_CARDS) {
        if (!active.current()) return;
        const chunk = ids.slice(index, index + BATCH_TRANSFER_MAX_APPLY_CARDS);
        const response = await api.applyCorrections(
          batchId,
          file,
          chunk,
          preview.previewToken,
          active.signal,
        );
        if (!active.current()) return;
        if (!response.ok) {
          const id = reportReference(response.body);
          if (id) onReport?.(id);
          throw new Error(response.message ?? response.error);
        }
        if (response.data.reportId) onReport?.(response.data.reportId);
        const allowed = new Set(chunk);
        for (const row of response.data.rows)
          if (allowed.has(row.correctionId)) confirmed.set(row.correctionId, row);
        setOutcomes([...confirmed.values()]);
        if (chunk.some((id) => !confirmed.has(id)))
          throw new Error('Some selected correction outcomes could not be confirmed.');
        setNotice(`Checked ${confirmed.size} of ${ids.length} selected corrections.`);
      }
      setNotice(
        'Selected corrections checked. Review the saved outcomes below. Card identifications were not changed.',
      );
    } catch (cause) {
      if (active.current())
        setError(
          `${cause instanceof Error ? cause.message : 'The correction import stopped.'} ${confirmed.size} outcomes confirmed. Preview the same file again before retrying; do not assume unconfirmed changes failed.`,
        );
    } finally {
      active.done();
      if (active.current()) {
        setPreview(null);
        setSelected(new Set());
        setBusy(null);
        onApplying(false);
      }
    }
  };
  return (
    <section aria-label="Checklist correction upload">
      <p>
        Upload the separate checklist corrections JSON. Preview compares exact source rows and
        values. An administrator applies selected changes to the shared checklist; accepting card
        identifications does not apply these corrections.
      </p>
      <p>
        To reverse a correction, supply a new correction file containing a REVERSE entry, the
        original correction ID and a reason. Preview and administrator approval are required.
      </p>
      <label>
        Checklist corrections JSON{' '}
        <input
          type="file"
          accept=".json,application/json"
          disabled={busy !== null || disabled}
          onChange={(event) => {
            const source = event.currentTarget.files?.[0];
            event.currentTarget.value = '';
            if (source) void upload(source);
          }}
        />
      </label>
      <div className="tcvs-transfer-feedback" aria-live="polite">
        {error ? (
          <p role="alert" className="tcvs-error">
            {error}
          </p>
        ) : null}
        {notice ? <p role="status">{notice}</p> : null}
        {busy ? (
          <p role="status">
            {busy === 'PREVIEW'
              ? 'Checking checklist corrections…'
              : 'Applying selected checklist corrections…'}
          </p>
        ) : null}
      </div>
      {preview && file ? (
        <>
          <p>
            Run {file.runId} · {selected.size} selected corrections
          </p>
          <div className="tcvs-transfer-table">
            <table>
              <thead>
                <tr>
                  <th>Apply</th>
                  <th>Correction / reason</th>
                  <th>Current</th>
                  <th>Proposed</th>
                  <th>Result</th>
                </tr>
              </thead>
              <tbody>
                {preview.rows.map((row) => {
                  const change = file.changes.find(
                    (item) => item.correctionId === row.correctionId,
                  );
                  return (
                    <tr key={row.correctionId}>
                      <td>
                        <input
                          type="checkbox"
                          aria-label={`Apply correction ${row.correctionId}`}
                          checked={selected.has(row.correctionId)}
                          disabled={busy !== null || row.status !== 'READY' || !admin}
                          onChange={(event) => {
                            const checked = event.currentTarget.checked;
                            setSelected((old) => {
                              const next = new Set(old);
                              if (checked) next.add(row.correctionId);
                              else next.delete(row.correctionId);
                              return next;
                            });
                          }}
                        />
                      </td>
                      <td>
                        {row.correctionId}
                        <p>
                          {change?.operation === 'REVERSE'
                            ? `Reverse ${change.reversesCorrectionId}`
                            : row.source
                              ? `${row.source.targetId} / ${row.source.itemId}`
                              : null}
                        </p>
                        <p>{change?.reason}</p>
                      </td>
                      <td>
                        <Values value={row.current} />
                      </td>
                      <td>
                        <Values value={row.proposed} />
                      </td>
                      <td>
                        <strong>{row.status.replaceAll('_', ' ')}</strong>
                        <p>{row.code}</p>
                        <p>{row.message}</p>
                        <p>{row.resolution}</p>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {admin ? (
            <button
              type="button"
              disabled={busy !== null || disabled || selected.size === 0}
              onClick={() => void apply()}
            >
              Apply checklist corrections ({selected.size})
            </button>
          ) : (
            <p>An administrator must apply the selected checklist corrections.</p>
          )}
        </>
      ) : null}
      {outcomes.length ? (
        <ul aria-label="Checklist correction outcomes">
          {outcomes.map((row) => (
            <li key={row.correctionId}>
              {row.correctionId} · {row.status.replaceAll('_', ' ')}
              {row.message ? ` · ${row.message}` : ''}
              {row.resolution ? <p>{row.resolution}</p> : null}
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}
